/**
 * These helper functions were taken from the storage one github:
 * https://github.com/inter-ikea/cbf-re-ipex-storageone-planner/blob/9d76087044b8ed0194da919cd6c013b29b5ef076/packages/app/src/rangeConfig/pax/guide/utils.tsx.
 */
import { useMemo } from 'react';
import { DEEP_CONFIG, SHALLOW_CONFIG } from '../../utils';
/**
 * All possible widths for a depth of 350mm.
 */
export const WIDTHS_35_CM = [88, 113, 138, 163, 188, 213, 238, 263, 288, 313, 338, 363, 388, 413];
/**
 * All possible widths for a depth of 580mm.
 */
export const WIDTHS_58_CM = [85, 110, 135, 160, 185, 210, 235, 260, 285, 310, 335, 360, 385, 410];
const getDefaultWallValues = (depth1, depth2) => {
    let wallOneValues = [];
    let wallTwoValues = [];
    //lower frame has depth 35 and upper frame has depth 35
    if (depth1 === SHALLOW_CONFIG && depth2 === SHALLOW_CONFIG) {
        wallOneValues = WIDTHS_35_CM;
        wallTwoValues = WIDTHS_35_CM;
        // lower frame has 58 and upper frame has 35 depth
    }
    else if (depth1 === SHALLOW_CONFIG && depth2 === DEEP_CONFIG) {
        wallOneValues = WIDTHS_58_CM;
        wallTwoValues = WIDTHS_35_CM;
    }
    else if (depth1 === DEEP_CONFIG && depth2 === DEEP_CONFIG) {
        // lower frame has 58 and upper frame has depth 58
        wallOneValues = WIDTHS_58_CM;
        wallTwoValues = WIDTHS_58_CM;
        // lower frame has depth 35 and upper frame has 58
    }
    else if (depth1 === DEEP_CONFIG && depth2 === SHALLOW_CONFIG) {
        wallOneValues = WIDTHS_35_CM;
        wallTwoValues = WIDTHS_58_CM;
    }
    return { wallOneValues, wallTwoValues };
};
/**
 * Find the closest value in a given array of values.
 * @param values The available values.
 * @param value The value we want to be close to.
 * @returns The closest value from the array.
 */
export const findClosest = (values, value) => {
    return values.reduce((a, b) => {
        const aDiff = Math.abs(a - value);
        const bDiff = Math.abs(b - value);
        if (aDiff === bDiff) {
            return a > b ? a : b;
        }
        else {
            return bDiff < aDiff ? b : a;
        }
    });
};
/**
 * Get the available widths for deep x deep configuration
 * @param currentWallWidth1 Wall 1 current width
 * @param currentWallWidth2 Wall 2 current width
 * @returns Allowed wall widths for both sections
 */
const getWidthsDeepDeep = (currentWallWidth1, currentWallWidth2) => {
    if (currentWallWidth2 === 85 || currentWallWidth2 === 135) {
        return {
            wallOneValues: WIDTHS_58_CM.filter(w => w !== 85 && w !== 135),
            wallTwoValues: WIDTHS_58_CM
        };
    }
    else if (currentWallWidth1 === 85 || currentWallWidth1 === 135) {
        return {
            wallOneValues: WIDTHS_58_CM,
            wallTwoValues: WIDTHS_58_CM.filter(w => w !== 85 && w !== 135)
        };
    }
    return getDefaultWallValues(DEEP_CONFIG, DEEP_CONFIG);
};
/**
 * Get the available widths for deep x shallow configuration
 * @param currentWallWidth1 Wall 1 current width
 * @param currentWallWidth2 Wall 2 current width
 * @returns Allowed wall widths for both sections
 */
const getWidthsDeepShallow = (currentWallWidth1, currentWallWidth2) => {
    if (currentWallWidth1 === 85) {
        // Impossible case, figure out if needed or not.
        // Left it here because it exists in PAX.
        // 850 mm will never be available for width1 when depth2 === SHALLOW_CONFIG
        return {
            wallOneValues: [88, 138, 163, 188, 213, 238, 263, 288, 313, 338, 363, 388, 413],
            wallTwoValues: WIDTHS_58_CM
        };
    }
    else if (currentWallWidth2 === 85) {
        return {
            wallOneValues: WIDTHS_35_CM.filter(w => w !== 113),
            wallTwoValues: WIDTHS_58_CM
        };
    }
    else if (currentWallWidth1 === 113) {
        return {
            wallOneValues: WIDTHS_35_CM,
            wallTwoValues: WIDTHS_58_CM.filter(w => w !== 85 && w !== 135)
        };
    }
    else if (currentWallWidth2 === 135) {
        return {
            wallOneValues: WIDTHS_35_CM.filter(w => w !== 113),
            wallTwoValues: WIDTHS_58_CM.filter(w => w !== 85)
        };
    }
    return getDefaultWallValues(DEEP_CONFIG, SHALLOW_CONFIG);
};
/**
 * Get the available widths for shallow x deep configuration
 * @param currentWallWidth1 Wall 1 current width
 * @param currentWallWidth2 Wall 2 current width
 * @returns Allowed wall widths for both sections
 */
const getWidthsShallowDeep = (currentWallWidth1, currentWallWidth2) => {
    if (currentWallWidth2 === 113) {
        return {
            wallOneValues: WIDTHS_58_CM.filter(w => w !== 85 && w !== 135),
            wallTwoValues: WIDTHS_35_CM
        };
    }
    else if (currentWallWidth1 === 85 || currentWallWidth1 === 135) {
        return {
            wallOneValues: WIDTHS_58_CM,
            wallTwoValues: WIDTHS_35_CM.filter(w => w !== 113)
        };
    }
    return getDefaultWallValues(SHALLOW_CONFIG, DEEP_CONFIG);
};
/**
 * Get the available widths for shallow x shallow configuration
 * @param currentWallWidth1 Wall 1 current width
 * @param currentWallWidth2 Wall 2 current width
 * @returns Allowed wall widths for both sections
 */
const getWidthsShallowShallow = (currentWallWidth1, currentWallWidth2) => {
    if (currentWallWidth1 === 113) {
        return {
            wallOneValues: WIDTHS_35_CM,
            wallTwoValues: WIDTHS_35_CM.filter(w => w !== 113)
        };
    }
    else if (currentWallWidth2 === 113) {
        return {
            wallOneValues: WIDTHS_35_CM.filter(w => w !== 113),
            wallTwoValues: WIDTHS_35_CM
        };
    }
    return getDefaultWallValues(SHALLOW_CONFIG, SHALLOW_CONFIG);
};
/**
 * Return the possible selections for the two walls in a corner depending on depth and width of the sections.
 * Some values are not possible so the widths array has to be updated for certain selections.
 *
 * @param currentDepth Main depth in mm.
 * @param currentDepthWall_2 Secondary depth in mm.
 * @param useMetricMeasures Should the numbers be in metric format.
 * @param currentWallWidth1 Main width in mm.
 * @param currentWallWidth2 Secondary width in mm.
 * @returns Possible selections for the corner configuration.
 */
export const useUpdatedWallValues = (currentDepth, currentDepthWall_2, currentWallWidth1, currentWallWidth2) => {
    return useMemo(() => {
        if (currentDepth === DEEP_CONFIG && currentDepthWall_2 === DEEP_CONFIG) {
            return getWidthsDeepDeep(currentWallWidth1, currentWallWidth2);
        }
        if (currentDepth === DEEP_CONFIG && currentDepthWall_2 === SHALLOW_CONFIG) {
            return getWidthsDeepShallow(currentWallWidth1, currentWallWidth2);
        }
        if (currentDepth === SHALLOW_CONFIG && currentDepthWall_2 === DEEP_CONFIG) {
            return getWidthsShallowDeep(currentWallWidth1, currentWallWidth2);
        }
        if (currentDepth === SHALLOW_CONFIG && currentDepthWall_2 === SHALLOW_CONFIG) {
            return getWidthsShallowShallow(currentWallWidth1, currentWallWidth2);
        }
        return getDefaultWallValues(currentDepth, currentDepthWall_2);
    }, [currentDepth, currentDepthWall_2, currentWallWidth1, currentWallWidth2]);
};
//# sourceMappingURL=WallWidthUtils.js.map